import { ReactNode, Reducer, useReducer } from 'react';
import { UIColor } from '../../components/ui/types';
import { generateUUID } from '../../utils/string';

type ToastType = Omit<UIColor, 'default'>;
export interface ToastItem {
  id: string;
  message: string;
  type?: ToastType;
  options?: ToastItemOptions;
}
export interface ToastItemOptions {
  icon?: ReactNode;

  /**
   * @default true
   */
  dismissible?: boolean;

  /**
   * @default 6000
   */
  displayDuration?: number | null;

  /**
   * Can be used to prevent duplicate toast items
   */
  uniqueKey?: string;

  /** Stable DOM id for E2E (maps to data-testid on the toast root) */
  dataTestId?: string;
}

export enum ToastActionType {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  CLEAR = 'CLEAR'
}

export type ToastAction = {
  type: ToastActionType;
  payload: string | ToastItem | null;
};

export type ToastState = {
  toastItems: ToastItem[];
};

export interface ToastStoreType extends ToastState {
  show: (message: string, type?: ToastType, options?: ToastItemOptions) => void;
  success: (message: string, options?: ToastItemOptions) => void;
  error: (message: string, options?: ToastItemOptions) => void;
  warning: (message: string, options?: ToastItemOptions) => void;
  primary: (message: string, options?: ToastItemOptions) => void;
  secondary: (message: string, options?: ToastItemOptions) => void;
  remove: (id: string, options?: ToastItemOptions) => void;
  clear: () => void;
}

export type ToastReducer = Reducer<ToastState, ToastAction>;

export const toastReducer: ToastReducer = (state, action) => {
  switch (action.type) {
    case 'ADD':
      const items = state.toastItems ? [...state.toastItems] : [];
      if (action.payload && typeof action.payload !== 'string') {
        if (action.payload.options?.uniqueKey) {
          // If item has unique key then check if one already exists with same key
          const uniqueKey = action.payload.options?.uniqueKey;
          const existing = items.find(
            (i) => i.options?.uniqueKey === uniqueKey
          );
          if (existing) {
            return { toastItems: items };
          }
        }
        items.push(action.payload);
      }
      return { toastItems: items };
    case 'REMOVE':
      return {
        toastItems: state.toastItems
          ? state.toastItems.filter((item) => item.id !== action.payload)
          : []
      };
    case 'CLEAR':
      return { toastItems: [] };

    default:
      return state;
  }
};

export default function useToastStore(): ToastStoreType {
  const [state, dispatch] = useReducer<ToastReducer>(toastReducer, {
    toastItems: []
  });

  const show = (
    message: string,
    type: ToastType = 'primary',
    options?: ToastItemOptions
  ) => {
    const opts = {
      dismissible: true,
      displayDuration: 6000,
      ...(options || {})
    };
    const uid = generateUUID();
    const item: ToastItem = {
      id: uid,
      message,
      type,
      options: opts
    };
    dispatch({ type: ToastActionType.ADD, payload: item });
  };

  const success = (message: string, options?: ToastItemOptions) =>
    show(message, 'success', options);
  const error = (message: string, options?: ToastItemOptions) =>
    show(message, 'error', options);
  const warning = (message: string, options?: ToastItemOptions) =>
    show(message, 'warning', options);
  const primary = (message: string, options?: ToastItemOptions) =>
    show(message, 'primary', options);
  const secondary = (message: string, options?: ToastItemOptions) =>
    show(message, 'secondary', options);

  const remove = (id: string) => {
    dispatch({ type: ToastActionType.REMOVE, payload: id });
  };

  const clear = () => {
    dispatch({ type: ToastActionType.CLEAR, payload: null });
  };

  return {
    toastItems: state.toastItems,
    show,
    success,
    error,
    warning,
    primary,
    secondary,
    remove,
    clear
  };
}
